import async from 'async';
import { Modal } from 'bootstrap';
import qs from 'qs';
import _ from 'underscore';

import { EnvironmentHelper } from '@biteinc/enums';

import { getMagicCopyTimeout, parseMagicCopyResults } from '../../helpers/magic_copy_helper';
import { cloneLocationOptionsSchema } from '../detail_views/schemas/clone_location_options_schema';
import { configurationAuditSchema } from './run_configuration_audit_schema';

app.LocationToolsView = app.BaseToolsView.extend({
  initialize(options) {
    this.options = options || {};
    this.listenTo(app.location, 'change', this.render);

    if (app.location.get('flashEnabled') && app.sessionUser.isBiteSupport()) {
      this.listenToOnce(app.externalAuthStrategy, 'destroy', () => {
        window.location.reload();
      });
    }
  },

  _magicCopyButtonWasClicked(event) {
    const $button = $(event.target);
    async.auto(
      {
        selectedLocations: (cb) => {
          const {
            modal: { footer },
            modelListView: locationListView,
          } = app.showLocationSelector(
            {
              modalTitle: 'Select destination locations to copy to (Step 1/2)',
              locationsUrl: '/api/v2/magic-copy/locations',
              locationGroupsUrl: '/api/v2/magic-copy/location-groups',
              maxSelectable: null,
            },
            cb,
          );
          const locationIds = locationListView.collection
            .getModels()
            .filter((location) => location.get('menuReferenceLocationId') === app.location.id)
            .map((location) => location.id);
          if (locationIds.length) {
            const $referenceLocationsButton = footer.addButton(
              'Select All That Reference This Location',
              'referencing-locations',
              true,
            );
            $referenceLocationsButton.click(() => {
              locationListView.selectModelIds(locationIds);
            });
          }
        },
        options: [
          'selectedLocations',
          (results, cb) => {
            const posI9nSchema = app.location.getPosI9nSchema();
            const detailsView = new app.MagicCopyOptionsDetailsView({
              callback(model) {
                cb(null, model.options);
              },
              locationHasPos: !!posI9nSchema,
              locationHasPosThatUsesManualTaxProfiles:
                posI9nSchema && posI9nSchema.usesManualTaxProfiles, // If we ever try to enable magic copy for multi-vendor, then we'll have to check all of the POS I9n schemas
            });
            app.modalManager.showModalWithView(detailsView);
          },
        ],
      },
      (err, results) => {
        const confirmed = app.promptUserToTypeInConfirmation(
          'Please type in "Magic Copy" to confirm that you want to perform magic copy to the locations you selected.',
          'magic copy',
        );
        if (!confirmed) {
          return;
        }

        const ourReferenceLocationId = app.location.get('menuReferenceLocationId');
        const toLocationsThatDoNotReferenceUsOrOurReference = results.selectedLocations.filter(
          (location) => {
            const referenceId = location.get('menuReferenceLocationId');
            if (!referenceId) {
              return false;
            }
            return referenceId !== ourReferenceLocationId && referenceId !== app.location.id;
          },
        );
        if (toLocationsThatDoNotReferenceUsOrOurReference.length) {
          const locationNames = toLocationsThatDoNotReferenceUsOrOurReference
            .map((location) => location.get('name'))
            .join(', ');
          const warningConfirmed = app.promptUserToTypeInConfirmation(
            'WARNING: You are about to copy to the following locations which have a reference different from this Location!' +
              '\n\n' +
              locationNames +
              '\n\n' +
              'If you wish to continue with the copy type "Copy Anyway":\n\n',
            'Copy Anyway',
            locationNames,
          );
          if (!warningConfirmed) {
            return;
          }
        }

        const reqId = $button.initLoadingButton($button.html(), 'Copying', 'Copied');
        const url = '/api/v2/magic-copy/location';

        let onCreateModal;

        function sendMagicCopyRequest() {
          app.MultiQueueHelper.makeRequest(
            url,
            {
              toLocationIds: results.selectedLocations.map((location) => location.id),
              options: results.options,
            },
            false,
            (err, finishData) => {
              if (err) {
                $button.loadingDidFinishWithError(reqId);
                return;
              }
              if (onCreateModal) {
                Modal.getOrCreateInstance(onCreateModal.el).dispose();
                onCreateModal = null;
              }

              const resultsStr = parseMagicCopyResults(finishData);

              if (EnvironmentHelper.isCurrentDev()) {
                // Only display the "Redo" option in dev environments to make testing easier.
                // It isn't useful in other environments since redoing a magic copy will not change
                // the result.
                new app.AlertView().show(
                  resultsStr,
                  null,
                  () => {
                    sendMagicCopyRequest();
                  },
                  () => {},
                  'Redo?',
                );
              } else {
                new app.AlertView().show(resultsStr);
              }
            },
            (err, createData) => {
              if (err) {
                $button.loadingDidFinishWithError(reqId);
                return;
              }

              const timeoutStr = getMagicCopyTimeout(createData);

              $button.loadingDidFinishSuccessfully(reqId);
              onCreateModal = new app.AlertView();
              onCreateModal.show(
                `Magic Copy in progress!\nThe results should be displayed within ${timeoutStr} minutes, but may take longer for large menus.\nA copy will be sent to your email.`,
              );
            },
          );
        }

        sendMagicCopyRequest();
      },
    );
  },

  _runConfigurationAuditButtonWasClicked(event) {
    const isDisabled = true;
    if (isDisabled) {
      new app.AlertView().show(
        'This feature is not yet designed to work with Orders API v2/v3. Please reach out to eng if you need to use this.',
      );
      return;
    }

    const $button = $(event.target);
    const detailsView = new app.SchemaDetailsView({
      title: 'Select Audit Types to Run',
      saveButtonTitles: [' Run Configuration Audit', 'Auditing', 'Done!'],
      subProperty: 'options',
      schema: {
        fields: configurationAuditSchema.fields.options.fields,
      },
      callback(model) {
        const reqId = $button.initLoadingButton($button.html(), 'Auditing', 'Done');
        const url = `${app.location.url()}/configuration-audit`;
        app.QueueHelper.makeRequest(
          'POST',
          url,
          {
            locationId: app.location.id,
            options: model.options,
          },
          true,
          (err, res) => {
            if (err) {
              $button.loadingDidFinishWithError(reqId);
            } else {
              $button.loadingDidFinishSuccessfully(reqId);
              const resultsView = new app.MenuAuditResultListView({
                results: res,
              });
              let totalSuccess = 0;
              let totalFail = 0;
              _.each(res, (resultSection) => {
                if (_.size(resultSection)) {
                  _.each(resultSection, (result) => {
                    if (result.success) {
                      totalSuccess += 1;
                    } else {
                      totalFail += 1;
                    }
                  });
                }
              });
              const modallableView = new app.ModallableView({
                views: [resultsView],
                title: `Audit Results (${totalSuccess}/${totalFail + totalSuccess})`,
              });
              app.modalManager.showModalWithView(modallableView);
            }
          },
        );
      },
    });
    app.modalManager.showModalWithView(detailsView);
  },

  _cloneLocationButtonWasClicked(event) {
    const $button = $(event.target);
    async.auto(
      {
        selectedLocations: (cb) => {
          app.showLocationSelector(
            {
              modalTitle: 'Select a source location to clone to here (Step 1/2)',
              locationsUrl: `/api/locations?${qs.stringify({
                filter: 'clone',
                locationId: app.location.id,
              })}`,
              maxSelectable: 1,
            },
            cb,
          );
        },
        options: [
          'selectedLocations',
          (results, cb) => {
            const detailsView = new app.SchemaDetailsView({
              schema: cloneLocationOptionsSchema,
              title: 'Select attributes to copy over (Step 2/2)',
              saveButtonTitles: ['Clone', 'Cloning', 'Done!'],
              subProperty: 'options',
              callback: (model) => {
                cb(null, model.options || {});
              },
            });
            app.modalManager.showModalWithView(detailsView);
          },
        ],
      },
      (err, results) => {
        const { selectedLocations, options } = results;
        if (options.copyIntegrations && selectedLocations[0].isLive()) {
          const confirmed = app.promptUserToTypeInConfirmation(
            'Please type in "Clone live integrations" to confirm that you want to copy over all integrations from this live location. It means Kiosk Preview will be able to send orders to a LIVE kitchen unless you change the credentials.',
            'clone live integrations',
          );
          if (!confirmed) {
            return;
          }
        }

        const confirmed = app.promptUserToTypeInConfirmation(
          'Please type in "Clone" to confirm that you want to wipe all the data on this location and clone it from the one you selected.',
          'clone',
        );
        if (!confirmed) {
          return;
        }

        const reqId = $button.initLoadingButton($button.html(), 'Cloning', 'Cloned');
        app.postRequest(
          `${app.location.url()}/clone`,
          {
            fromLocationId: selectedLocations[0].id,
            options,
          },
          () => {
            $button.loadingDidFinishSuccessfully(reqId);
            app.showSavedToastAndReload('Cloned!', true);
          },
          () => {
            $button.loadingDidFinishWithError(reqId);
          },
        );
      },
    );
  },

  _runDataAuditButtonWasClicked(event) {
    const $button = $(event.target);
    const reqId = $button.initLoadingButton($button.html(), 'Auditing', 'Audited!');
    const url = `${app.location.url()}/data-audit`;
    app.makeRequest(
      'POST',
      url,
      null,
      (data) => {
        $button.loadingDidFinishSuccessfully(reqId);
        if (data.summary) {
          new app.AlertView().show(data.summary, null, () => {
            window.location.reload();
          });
        }
      },
      () => {
        $button.loadingDidFinishWithError(reqId);
      },
    );
  },

  _runQuizAuditButtonWasClicked(event) {
    const $button = $(event.target);
    const reqId = $button.initLoadingButton($button.html(), 'Auditing', 'Audited!');
    const url = `${app.location.url()}/quiz-audit`;
    app.makeRequest(
      'POST',
      url,
      null,
      (data) => {
        $button.loadingDidFinishSuccessfully(reqId);
        if (data.summary) {
          new app.AlertView().show(data.summary, null, () => {});
        }
      },
      () => {
        $button.loadingDidFinishWithError(reqId);
      },
    );
  },

  _runRefreshDayPartsButtonWasClicked(event) {
    const $button = $(event.target);
    const reqId = $button.initLoadingButton($button.html(), 'Refreshing', 'Refreshed!');
    const url = `/api/v2/locations/${app.location.id}/refresh-day-parts`;
    app.postRequest(
      url,
      null,
      () => {
        $button.loadingDidFinishSuccessfully(reqId);
        window.location.reload();
      },
      () => {
        $button.loadingDidFinishWithError(reqId);
      },
    );
  },

  _removeArchiveButtonWasClicked(event) {
    const maybeLive = app.location.isLive() ? 'LIVE ' : '';
    const confirmed = app.promptUserToTypeInConfirmation(
      `Please type in "Delete the Archive" to confirm that you want to delete all archived menu items, sections, mods, and mod groups at this ${maybeLive}location.`,
      'Delete the Archive',
    );
    if (!confirmed) {
      return;
    }

    const $button = $(event.target);
    const reqId = $button.initLoadingButton($button.html(), 'Removing', 'Removed');
    app.location.removeArchive({
      error() {
        $button.loadingDidFinishWithError(reqId);
      },
      success() {
        $button.loadingDidFinishSuccessfully(reqId);
        app.showSavedToastAndReload('Removed menu archive!', true);
      },
    });
  },

  _deleteMenuButtonWasClicked(event) {
    const msg =
      'Are you sure you want to delete the menu?\nThis will delete ' +
      'all items, sections, mods, etc.';
    if (!window.confirm(msg)) {
      return;
    }

    const $button = $(event.target);
    const reqId = $button.initLoadingButton($button.html(), 'Deleting', 'Deleted');
    app.location.deleteMenu({
      error() {
        $button.loadingDidFinishWithError(reqId);
      },
      success() {
        $button.loadingDidFinishSuccessfully(reqId);
        app.showSavedToastAndReload('Deleted the menu!', true);
      },
    });
  },

  _invalidateMenuCacheButtonWasClicked(event) {
    if (!window.confirm('Are you sure you want to invalidate the menu cache?')) {
      return;
    }

    const $button = $(event.target);
    const reqId = $button.initLoadingButton($button.html(), 'Invalidating', 'Invalidated');
    app.location.refreshMenuCache({
      error() {
        $button.loadingDidFinishWithError(reqId);
      },
      success() {
        $button.loadingDidFinishSuccessfully(reqId);
        app.showSavedToastAndReload('Clearing the cache!');
      },
    });
  },

  _zeroPricePosDefaultModsWasClicked(event) {
    const $button = $(event.target);
    void async.auto({
      selectedMenuSections: (cb) => {
        const maxSelectable = null;
        const modelListView = new app.ModelSelectorListView({
          collection: app.menuSectionList,
          maxSelectable,
        });
        app.showModelSelector(
          {
            modalTitle: `Select menu sections to modify`,
            modelType: 'Menu Sections',
            modelListView,
            maxSelectable,
          },
          cb,
        );
      },
      options: [
        'selectedMenuSections',
        (results) => {
          const menuSectionIds = results.selectedMenuSections.map((section) => section.get('_id'));
          if (
            !window.confirm(
              'Are you sure you want to set all POS default mods to $0 under these sections?',
            )
          ) {
            return;
          }

          const reqId = $button.initLoadingButton($button.html(), 'Updating...', 'Updated!');
          const url = `/api/v2/locations/${app.location.id}/set-pos-default-mods-to-zero`;
          app.postRequest(
            url,
            {
              menuSectionIds,
            },
            () => {
              $button.loadingDidFinishSuccessfully(reqId);
              window.location.reload();
            },
            () => {
              $button.loadingDidFinishWithError(reqId);
            },
          );
        },
      ],
    });
  },

  _deleteLocationButtonWasClicked(event) {
    if (
      !window.confirm(
        'Are you sure you want to delete this location? The deletion will be processed in the background and you will be notified via email when it completes. The location may still be accessible in the meantime.',
      )
    ) {
      return;
    }

    const $button = $(event.target);
    const reqId = $button.initLoadingButton($button.html(), 'Requesting', 'Requested');
    app.location.destroy({
      error() {
        $button.loadingDidFinishWithError(reqId);
      },
      success(data) {
        $button.loadingDidFinishSuccessfully(reqId);
        const message = data.status || 'Deletion request added to queue!';
        app.showSavedToastAndReload(message);
        setTimeout(() => {
          window.location = app.site.bureauUrl();
        }, 1500);
      },
    });
  },

  _syncOpenHoursButtonWasClicked(event) {
    const $button = $(event.target);

    const reqId = $button.initLoadingButton($button.html(), 'Syncing...', 'Synced!');
    const url = `${app.location.url()}/sync-open-hours`;

    app.QueueHelper.makeRequest('POST', url, {}, true, (err) => {
      if (err) {
        $button.loadingDidFinishWithError(reqId);
        return;
      }

      $button.loadingDidFinishSuccessfully(reqId);
      window.location.reload();
    });
  },

  _compareLocationsButtonWasClicked(event) {
    const $button = $(event.target);
    async.auto(
      {
        selectedLocations: (cb) => {
          app.showLocationSelector(
            {
              modalTitle: 'Select other locations to compare',
              locationsUrl: '/api/v2/compare/locations',
              locationGroupsUrl: '/api/v2/compare/location-groups',
              maxSelectable: null,
            },
            cb,
          );
        },
      },
      (err, results) => {
        const reqId = $button.initLoadingButton($button.html(), 'Comparing', 'Compared');
        const url = '/api/v2/compare/locations';
        app.QueueHelper.makeRequest(
          'POST',
          url,
          {
            locationIds: results.selectedLocations.map((location) => location.id),
          },
          true,
          (err) => {
            if (err) {
              $button.loadingDidFinishWithError(reqId);
            } else {
              $button.loadingDidFinishSuccessfully(reqId);
              app.showSavedToast('Check email for comparison reports');
            }
          },
        );
      },
    );
  },

  render() {
    app.BaseToolsView.prototype.render.apply(this, arguments);

    if (app.sessionUser.canMagicCopyLocation()) {
      this.addToolbarButton(
        'btn-danger',
        ' ✨Magic Copy to... ✨',
        this._magicCopyButtonWasClicked.bind(this),
      );
    }

    if (app.sessionUser.isBiteSupport()) {
      this.addToolbarButton(
        'btn-primary',
        'Audit & Fix Data',
        this._runDataAuditButtonWasClicked.bind(this),
      );

      this.addToolbarButton(
        'btn-primary',
        'Audit Quizzes',
        this._runQuizAuditButtonWasClicked.bind(this),
      );

      this.addToolbarButton(
        'btn-primary',
        'Refresh Day Parts',
        this._runRefreshDayPartsButtonWasClicked.bind(this),
      );

      this.addToolbarButton(
        'btn-primary',
        'Sync Open Hours from Google',
        this._syncOpenHoursButtonWasClicked.bind(this),
      );

      this.addToolbarButton(
        'btn-primary',
        'Audit Menu/Configuration on the POS',
        this._runConfigurationAuditButtonWasClicked.bind(this),
      );

      this.addToolbarButton(
        'btn-primary',
        'Compare Locations',
        this._compareLocationsButtonWasClicked.bind(this),
      );

      this.addToolbarButton(
        'btn-danger',
        'Invalidate Menu Cache',
        this._invalidateMenuCacheButtonWasClicked.bind(this),
      );

      this.addToolbarButton(
        'btn-danger',
        'Override Price to $0 for POS Default Mods',
        this._zeroPricePosDefaultModsWasClicked.bind(this),
      );

      if (!app.location.isLive()) {
        this.addToolbarButton(
          'btn-danger',
          'Clone Location from...',
          this._cloneLocationButtonWasClicked.bind(this),
        );
      }

      this.addToolbarButton(
        'btn-danger',
        '🗑 Remove Archived Menu Things',
        this._removeArchiveButtonWasClicked.bind(this),
      );
    }

    if (app.sessionUser.isBiteOps()) {
      this.addToolbarButton(
        'btn-danger',
        'Delete This Menu',
        this._deleteMenuButtonWasClicked.bind(this),
      );

      this.addToolbarButton(
        'btn-danger',
        'Delete This Location',
        this._deleteLocationButtonWasClicked.bind(this),
      );
    }

    if (app.location.get('flashEnabled') && app.sessionUser.isBiteSupport()) {
      if (app.externalAuthStrategy.hasBeenFetched()) {
        const view = new app.AnchoredModalView({
          view: new app.BaseDetailsView({
            model: app.externalAuthStrategy,
            schema: app.externalAuthStrategy.Schema,
          }),
          isPermanent: true,
        });
        this.$el.append(view.render().$el);
      } else {
        this.listenToOnce(app.externalAuthStrategy, 'reset', this.render);
        app.externalAuthStrategy.fetch({ reset: true });
      }
    }

    return this;
  },
});
